import React, { forwardRef } from 'react';
import { Box, TextField, InputAdornment, IconButton, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * SearchBar Component
 * 
 * A search input component with icon, clear button, and result count display.
 * Designed for filtering navigation cards on the Home page.
 * 
 * @param {Object} props
 * @param {string} props.value - Current search query value
 * @param {function} props.onChange - Callback when search value changes
 * @param {number} props.resultCount - Number of filtered results
 * @param {string} props.placeholder - Placeholder text for input (optional)
 * @param {Object} props.themeStyles - Theme-specific styling object (optional)
 * @param {boolean} props.animationsEnabled - Whether animations are enabled
 * @param {React.Ref} ref - Ref to the input element for programmatic focus
 */
const SearchBar = forwardRef(({
  value,
  onChange,
  resultCount,
  placeholder = 'Search navigation...',
  themeStyles = {},
  animationsEnabled = true,
}, ref) => {
  const [prevResultCount, setPrevResultCount] = React.useState(resultCount);
  const [isCountChanging, setIsCountChanging] = React.useState(false);

  // Animate result count changes
  React.useEffect(() => {
    if (resultCount !== prevResultCount && animationsEnabled) {
      setIsCountChanging(true);
      const timer = setTimeout(() => {
        setIsCountChanging(false);
        setPrevResultCount(resultCount);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setPrevResultCount(resultCount);
    }
  }, [resultCount, prevResultCount, animationsEnabled]);
  const handleClear = () => {
    onChange('');
  };

  const handleChange = (event) => {
    onChange(event.target.value);
  };

  return (
    <Box sx={{ 
      mb: 2,
      ...(animationsEnabled && {
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.02)' },
          '100%': { transform: 'scale(1)' },
        },
      }),
    }}>
      <TextField
        fullWidth
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        variant="outlined"
        // Use small size for consistency
        size="small"
        inputRef={ref}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ 
                color: 'action.active',
                fontSize: { xs: 24, sm: 20 },
                transition: animationsEnabled ? 'all 0.3s ease' : 'none',
                ...(value && animationsEnabled && {
                  color: 'primary.main',
                  transform: 'scale(1.1)',
                }),
              }} />
            </InputAdornment>
          ),
          endAdornment: value && (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search"
                onClick={handleClear}
                edge="end"
                size="small"
                sx={{
                  // Mobile: Ensure minimum touch target size
                  minWidth: { xs: 44, sm: 'auto' },
                  minHeight: { xs: 44, sm: 'auto' },
                  transition: animationsEnabled ? 'all 0.2s ease' : 'none',
                  ...(animationsEnabled && {
                    animation: 'fadeIn 0.3s ease',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'scale(0.8)' },
                      to: { opacity: 1, transform: 'scale(1)' },
                    },
                  }),
                }}
              >
                <ClearIcon sx={{ fontSize: { xs: 22, sm: 20 } }} />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            ...themeStyles.card,
            borderRadius: 2,
            // Mobile: Larger padding for better touch experience
            py: { xs: 0.5, sm: 0 },
            transition: animationsEnabled ? 'all 0.3s ease' : 'none',
            '& fieldset': {
              borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.23)',
              transition: animationsEnabled ? 'border-color 0.3s ease' : 'none',
            },
            '&:hover fieldset': {
              borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.23)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
        sx={{
          '& .MuiInputBase-input': {
            ...themeStyles.cardTitle,
            // Mobile: Larger font size
            fontSize: { xs: '16px', sm: '14px' },
            // Prevent zoom on iOS
            '@media (max-width: 600px)': {
              fontSize: '16px',
            },
          },
        }}
      />
      {value && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            ml: 1,
            opacity: 0.7,
            // Mobile: Slightly larger text
            fontSize: { xs: '13px', sm: '12px' },
            ...themeStyles.cardDescription,
            transition: animationsEnabled ? 'all 0.3s ease' : 'none',
            ...(animationsEnabled && isCountChanging && {
              animation: 'pulse 0.3s ease',
              color: 'primary.main',
              fontWeight: 600,
            }),
          }}
        >
          {resultCount} {resultCount === 1 ? 'result' : 'results'} found
        </Typography>
      )}
    </Box>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
